'use client';

import React, {
  memo,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';

import { isAndroid } from 'react-device-detect';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';

import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import { IconSearch02 } from '@wconcept/wconcept-ui-renewal/components/atoms/svg';

import * as S from './style';

interface IProps {
  /** id */
  id?: string;
  /** className */
  className?: string;
  /** type */
  type?: 'text' | 'number' | 'search' | 'password' | 'email' | undefined;
  /** name */
  name?: string;
  /** autoComplete */
  autoComplete?: string;
  /** disabled */
  disabled?: boolean;
  /** readOnly */
  readOnly?: boolean;
  /** value */
  value?: string | number;
  /** placeholder */
  placeholder?: string;
  /** maxLength */
  maxLength?: number;
  /** allClear */
  allClear?: boolean;
  /** searchBtn */
  searchBtn?: boolean;
  /** pattern */
  pattern?: string;
  /** inputMode */
  inputMode?: string;
  /** 맨 끝에 붙는 컨텐츠 */
  suffix?: React.ReactNode;
  /** onChange */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** onBlur */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** onFocus */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** onPressEnter */
  onPressEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** onSearch */
  onSearch?: (e: HTMLInputElement) => void;
  /** onKeyDown */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** onClear */
  onClear?: (e: HTMLInputElement) => void;
}

const Input = ({
  id,
  className,
  name = '',
  type = 'text',
  autoComplete = 'off',
  disabled,
  readOnly,
  value = '',
  placeholder = '',
  maxLength,
  onChange,
  onBlur,
  onFocus,
  onPressEnter,
  onKeyDown,
  onSearch,
  onClear,
  allClear = false,
  searchBtn = false,
  pattern,
  inputMode,
  suffix,
}: IProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(value);

  // 초기화
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const param = {
    className: useMemo(
      () =>
        classNameBind([
          'input',
          disabled ? 'disabled' : '',
          readOnly ? 'readOnly' : '',
          allClear ? 'allClear' : '',
          searchBtn ? 'searchBtn' : '',
          className || '',
        ]),
      [disabled, readOnly, allClear, searchBtn, className]
    ),
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paramInp: any = {
    id: id || undefined,
    name: name || undefined,
    type,
    autoComplete,
    disabled,
    readOnly,
    value: inputValue,
    placeholder,
    maxLength,
    ref,
    pattern,
    inputMode,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      if (maxLength && e?.target.value?.length > maxLength) {
        return;
      }

      setInputValue(e.target.value);
      if (onChange) {
        onChange(e);
      }
    },
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(e);
      }
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(e);
      }
    },
    onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        if (onPressEnter) {
          onPressEnter(e);
        }
      }
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown) {
        onKeyDown(e);
      }
    },
  };

  // EVENT HANDLER : 검색 버튼
  const onClickSearch = useCallback(() => {
    const el = ref.current as HTMLInputElement;
    if (onSearch) {
      onSearch(el);
      return;
    }
    if (el) {
      el.dispatchEvent(
        new KeyboardEvent('keypress', {
          bubbles: true,
          cancelable: true,
          key: 'Enter',
          shiftKey: false,
          keyCode: 13,
        })
      );
    }
    // console.log(ref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSearch]);

  // EVENT HANDLER : 텍스트 삭제
  const onClickClear = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    const el = ref.current as any;
    if (el) {
      if (onClear) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        onClear(el);
      }
      setInputValue('');
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const lastValue = el.value;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      el.value = '';
      // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-unsafe-member-access
      if (el._valueTracker) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, no-underscore-dangle
        el?._valueTracker.setValue(lastValue);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      el.dispatchEvent(new Event('input', { bubbles: true }));
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      el.focus();
    }
    // console.log(ref);
  }, [onClear]);

  // ITEM RENDERER : getInpRender
  const getInpRender = useCallback(
    () => (
      <S.Box className="input-wrapper">
        <S.Input value={inputValue} {...paramInp} lang="ko" />
        {allClear && (
          <Button
            className="btn-clear"
            label="텍스트 삭제"
            onClick={onClickClear}
          >
            <span className="inner" />
          </Button>
        )}
        {suffix && <span className="inp-suffix">{suffix}</span>}
      </S.Box>
    ),
    [allClear, inputValue, onClickClear, paramInp, suffix]
  );

  return (
    <S.Layout {...param}>
      {getInpRender()}
      {searchBtn && (
        <Button
          className="btn-serach"
          label="텍스트 검색"
          icon={<IconSearch02 />}
          onClick={onClickSearch}
        />
      )}
    </S.Layout>
  );
};

export default memo(Input);
